import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, Grid
} from '@mui/material';
import { skillService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const CategoryFormModal = ({ open, onClose, category, onSuccess }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        category_name: '',
        description: ''
    });

    useEffect(() => {
        if (open) {
            if (category) {
                // Edit mode - populate form with existing data
                setFormData({
                    category_name: category.category_name || '',
                    description: category.description || ''
                });
            } else {
                // Create mode - reset form
                setFormData({
                    category_name: '',
                    description: ''
                });
            }
            setError('');
        }
    }, [category, open]);
    
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) {
            setError('');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        let trimmedCategoryName = formData.category_name.trim();
        if (!formData.category_name.trim()) {
            showToast('Category name is required', 'error');
            return;
        }

        setLoading(true);
        try {
            const submitData = {
                category_name: trimmedCategoryName,
                description: formData.description.trim()
            };
            if (category) {
                // Edit mode
                await skillService.updateCategory(category.id, submitData);
                showToast('Category updated successfully', 'success');
            } else {
                // Create mode
                await skillService.createCategory(submitData);
                showToast('Category created successfully', 'success');
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating category:', error);
            const message = error.response?.data?.message || 'Error creating category';
            showToast(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({ category_name: '', description: '' });
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {category ? 'Edit Category' : 'Add New Category'}
                </DialogTitle>
                <DialogContent 
                    dividers
                    sx={{                    
                        minHeight: 0,
                        overflowY: 'auto',
                        pt: 2,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                name="category_name"
                                label="Category Name"
                                value={formData.category_name}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="description"
                                label="Description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading}
                    >
                        {loading 
                            ? (category ? 'Updating...' : 'Creating...') 
                            : (category ? 'Update' : 'Create')
                        }
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CategoryFormModal;